import { useEffect, useState } from 'react';
import { useAuthStore as useAuthV2Store } from '@/Stores/auth-v2-store';
import { useNewsSummary, useNewsViewerId } from '../../hooks/useNewsReader';
import type { NewsItem, NewsPlatform } from '../../types/news.types';
import { NewsArrivalModal } from './NewsArrivalModal';
import { NewsBanner } from './NewsBanner';
import { NewsSilentBoundary } from './NewsSilentBoundary';

/**
 * Lecteurs à qui la modale a déjà été présentée depuis le chargement de la page
 * (clé `plateforme:lecteur`). Hors de React à dessein : un layout peut être
 * remonté par une navigation (la gestion SAV monte le sien sous deux branches de
 * routes) et la modale ne doit pas revenir pour autant. C'est aussi ce qui
 * l'empêche de se rouvrir quand le serveur n'enregistre rien (impersonation) :
 * dans ce cas le résumé rechargé contient encore ses diapos.
 */
const presentedModals = new Set<string>();

function ArrivalContent({
  platform,
  viewerId,
  blocked,
}: {
  platform: NewsPlatform;
  viewerId: string;
  blocked: boolean;
}) {
  const { data: summary } = useNewsSummary(platform);
  // Diapos FIGÉES à l'ouverture : le résumé est rechargé après chaque événement
  // de suivi et ne contient alors plus les nouveautés présentées — la modale ne
  // doit ni se vider ni se refermer sous les yeux de l'utilisateur.
  const [slides, setSlides] = useState<NewsItem[] | null>(null);
  const [closed, setClosed] = useState(false);

  const pendingSlides = !blocked && summary && summary.modal.length > 0 ? summary.modal : null;

  // Verrou d'ouverture, pas une dérivation : « la première fois que des diapos
  // sont présentables », on les fige et on note que ce lecteur a eu sa modale.
  useEffect(() => {
    if (!pendingSlides) return;
    const key = `${platform}:${viewerId}`;
    if (presentedModals.has(key)) return;
    presentedModals.add(key);
    setSlides(pendingSlides);
  }, [pendingSlides, platform, viewerId]);

  return (
    <>
      {!blocked && summary && summary.banner.length > 0 && (
        <NewsBanner platform={platform} viewerId={viewerId} items={summary.banner} />
      )}
      {slides && !closed && (
        <NewsArrivalModal platform={platform} slides={slides} onClose={() => setClosed(true)} />
      )}
    </>
  );
}

/**
 * Point de montage des nouveautés dans le layout d'une plateforme : le bandeau
 * (rendu à l'endroit du montage) et la modale d'arrivée (rendue dans
 * `document.body`). À ne monter que sur les pages avec navigation, pour un
 * utilisateur connecté.
 *
 * `blocked` : tant qu'il est vrai, ni modale ni bandeau, et donc aucun
 * `modal_delivered` — la modale apparaîtra quand il repassera à faux. L'outil
 * pro s'en sert tant qu'un bandeau Stripe est affiché OU que cet état n'est pas
 * encore connu. Une modale déjà ouverte n'est pas refermée.
 *
 * Sous impersonation (session v2), le blocage est appliqué d'office, quelle que
 * soit la plateforme.
 *
 * Rien n'est rendu si le résumé n'a pas pu être chargé.
 */
export function NewsArrival({ platform, blocked = false }: { platform: NewsPlatform; blocked?: boolean }) {
  const viewerId = useNewsViewerId(platform);
  // Même source que le bouton « quitter l'aperçu » de l'en-tête de l'outil pro :
  // le drapeau du store v2, posé par l'initialisation de la session à partir du
  // profil renvoyé par le serveur, AVANT que la session soit déclarée prête (et
  // donc avant que `viewerId` existe).
  const isImpersonatingV2 = useAuthV2Store((state) => state.isImpersonating);
  if (!viewerId) return null;

  // Décision produit : un interne qui navigue « en tant que » un utilisateur ne
  // voit NI la modale NI le bandeau (le fil, la page et le badge restent). Le
  // serveur ignorant tous ses événements, la modale reviendrait à chaque
  // rechargement. Bloquer ici garantit qu'aucun `modal_delivered` ne part : la
  // modale n'est jamais montée. Règle appliquée à toute plateforme en session
  // v2 ; la gestion SAV (jeton v1) ne connaît pas l'impersonation et ne lit pas
  // ce store.
  const isImpersonating = platform !== 'SAV' && isImpersonatingV2;

  return (
    <NewsSilentBoundary>
      {/* `key` : un autre compte dans le même onglet repart d'un état neuf. */}
      <ArrivalContent
        key={viewerId}
        platform={platform}
        viewerId={viewerId}
        blocked={blocked || isImpersonating}
      />
    </NewsSilentBoundary>
  );
}
